import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Header,} from "./UserList.styled";
import {ReactComponent as UserPlusIcon} from "../../../assets/icons/user-plus.svg";
import {ReactComponent as UserEditIcon} from "../../../assets/icons/user-edit.svg";
import {ReactComponent as UserDeleteIcon} from "../../../assets/icons/user-minus.svg";
import {Button, Pagination} from "@mui/material";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import ConfirmationDialog from "../../../components/dialogs/ConfirmationDialog";
import PageSizeSelector from "../../../components/common/PageSizeSelector";
import {parseError} from "../../../errors/http.service";
import {useTranslation} from 'react-i18next';
import SearchInput from "../../../components/common/SearchInput";
import ResponsiveTable, {ResponsiveTableColumn} from "../../../components/common/ResponsiveTable";
import {
    ControlsRow,
    PageSizeWrapper,
    PaginationWrapper,
    SearchWrapper,
    StyledTableContainer
} from "../../../components/common/TableContainer.styled";
import {usersService} from "../../../services/users.service";
import {User} from "../../../models/users.models";

const SEARCH_DEBOUNCE_MS = 400;

const UserList = () => {
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const [users, setUsers] = useState<User[]>([]);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const pageSizeOptions = [5, 10, 20, 50];
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

    useEffect(() => {
        const normalizedSearchValue = searchValue.trim();
        const normalizedDebouncedSearchValue = debouncedSearchValue.trim();

        if (normalizedSearchValue === normalizedDebouncedSearchValue) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [debouncedSearchValue, searchValue]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await usersService.fetchUsers(currentPage - 1, pageSize, debouncedSearchValue);

            setUsers(result.content);
            setTotalElements(result.totalElements || 0);
        } catch (error) {
            showSnackbar(parseError(error), "error");
            console.error("Błąd pobierania użytkowników:", error);

            setUsers([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearchValue, pageSize, showSnackbar]);

    useEffect(() => {
        void fetchData();
    }, [fetchData]);

    const navigateToEditUser = useCallback((user: User) => {
        navigate(`/admin/user/${user.id}/edit`);
    }, [navigate]);

    const openDeleteDialog = useCallback((user: User) => {
        setUserToDelete(user);
        setIsDeleteDialogOpen(true);
    }, []);

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            await usersService.deleteUser(userToDelete.email);
            await fetchData();
            showSnackbar(t("userList.userDeletedSuccess"), "success");
        } catch (error) {
            showSnackbar(parseError(error), "error");
            console.error("Błąd usuwania użytkownika:", error);
        } finally {
            setIsDeleteDialogOpen(false);
            setUserToDelete(null);
        }
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    const handleSearchChange = (value: string) => {
        setSearchValue(value);
    };

    const columns: ResponsiveTableColumn<User>[] = useMemo(() => [
        {
            id: 'username',
            header: t('userList.tableHeaders.username'),
            render: (u) => u.email,
        },
        {
            id: 'role',
            header: t('userList.tableHeaders.role'),
            render: (u) => t(`userList.roleLabels.${u.role}`),
        },
        {
            id: 'active',
            header: t('userList.tableHeaders.active'),
            render: (u) => (u.activated ? t('userList.yes') : t('userList.no')),
        },
        {
            id: 'actions',
            header: t('userList.tableHeaders.actions'),
            cellClassName: 'actions-cell',
            render: (u) => (
                <>
                    <Button 
                        variant={'outlined'} 
                        onClick={() => openDeleteDialog(u)}
                        aria-label={t('userList.deleteButton.ariaLabel', { username: u.email }) as string}
                    >
                        <UserDeleteIcon aria-hidden={true} focusable={false}/>
                        <span style={{marginLeft: '8px'}}>{t('userList.deleteButton')}</span>
                    </Button>
                    <Button 
                        variant={'outlined'} 
                        onClick={() => navigateToEditUser(u)}
                        aria-label={t('userList.editButton.ariaLabel', { username: u.email }) as string}
                    >
                        <UserEditIcon aria-hidden={true} focusable={false}/>
                        <span style={{marginLeft: '8px'}}>{t('userList.editButton')}</span>
                    </Button>
                </>
            ),
        },
    ], [t, openDeleteDialog, navigateToEditUser]);

    return (
        <StyledTableContainer role="region" aria-labelledby="user-list-title">
            <Header>
                <h2 id="user-list-title">
                    {t('userList.title')}
                </h2>
                <Button
                    variant={'contained'}
                    onClick={() => navigate("/admin/user/create")}
                    startIcon={<UserPlusIcon aria-hidden={true} focusable={false}/>}
                    aria-label={t('userList.addButton.ariaLabel') as string}
                >
                    {t('userList.addButton')}
                </Button>
            </Header>

            <ControlsRow>
                <SearchWrapper>
                    <SearchInput
                        onChange={handleSearchChange}
                        value={searchValue}
                        ariaLabel={t('userList.search.ariaLabel') as string}
                    />
                </SearchWrapper>

                <PageSizeWrapper>
                    <PageSizeSelector
                        options={pageSizeOptions}
                        selectedSize={pageSize}
                        onChange={handlePageSizeChange}
                        ariaLabelGroup={t('pageSizeSelector.aria.group') as string}
                        getSizeAriaLabel={(size) => t('pageSizeSelector.aria.setSize', { size }) as string}
                    />
                </PageSizeWrapper>
            </ControlsRow>

            <div aria-live="polite" aria-atomic={true} aria-busy={loading}>
                {loading && (
                    <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                        {t('userList.loading')}
                    </div>
                )}
                <ResponsiveTable
                    columns={columns}
                    data={users}
                    getRowKey={(u) => u.email}
                    emptyState={loading ? t('userList.loading') : t('userList.emptyState')}
                    ariaLabel={t('userList.table.ariaLabel') as string}
                />
            </div>

            {totalElements > pageSize && (
                <PaginationWrapper>
                    <Pagination
                        count={Math.ceil(totalElements / pageSize)}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        aria-label={t('userList.pagination.ariaLabel') as string}
                    />
                </PaginationWrapper>
            )}

            {userToDelete && (
                <ConfirmationDialog
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    onConfirm={confirmDeleteUser}
                    title={t('deleteUserDialog.title')}
                    message={<>{t('deleteUserDialog.message', {user: userToDelete.email})}</>}
                    confirmButtonText={t('deleteUserDialog.deleteButton') as string}
                    cancelButtonText={t('deleteUserDialog.cancelButton') as string}
                />
            )}
        </StyledTableContainer>
    );
};

export default UserList;
